export type ShareInviteResult = "shared" | "copied" | "canceled" | "failed";

export async function copyTextToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Clipboard API is commonly blocked on local HTTP hosts. Fall through.
    }
  }

  if (!document.body || typeof document.execCommand !== "function") return false;

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.readOnly = true;
  textarea.tabIndex = -1;
  textarea.setAttribute("aria-hidden", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  textarea.style.fontSize = "16px";

  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);

  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

export async function shareInviteOrCopy(
  data: ShareData,
): Promise<ShareInviteResult> {
  const canUseNativeShare = typeof navigator.share === "function"
    && (typeof navigator.canShare !== "function" || navigator.canShare(data));

  if (canUseNativeShare) {
    try {
      await navigator.share(data);
      return "shared";
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        return "canceled";
      }
      // If the share sheet is unavailable or blocked, copy the link instead.
    }
  }

  return data.url && await copyTextToClipboard(data.url) ? "copied" : "failed";
}
